import {SDrawer, SDrawerOrModal, SGeneralForms} from "shj-components";
import {useEffect, useRef, useState} from "react";
import {get, isArray, isEmpty} from "lodash-es";
import {Form, message} from "antd";
import {getLineAllocationList, getSeatIpConfigList, seatAccountSave, seatAccountUpdate} from "@/services/crmTool.ts";
import {trimStr} from "@/utils/data.ts";
import {getEnums, httpOrgList} from "@/services/homeService.ts";
import {getSeatWorkerList} from "@/services/saleOpServices.ts";
import {getUser} from "shj-utils";
import {getChildrenDicts} from "@/services";
import {departmentSearchList} from "@/services/customerServices.ts";

interface IAddOrEditProps {
  open?: boolean
  title?: string
  reload?: (values?: object, res?: any) => void
  onClose?: (values?: object) => void
  setOpen?: (open: boolean) => void
  onReload?: (values?: object) => void
  seatPerson?: any[]
  record?: any
}

export const AddOrEdit: React.FC<IAddOrEditProps> = (props: IAddOrEditProps) => {
  const { record, open, setOpen, onReload } = props
  const user = getUser()
  const [form] = Form.useForm();
  const [btnLoading, setBtnLoading] = useState(false)
  const [seatPerson, setSeatPerson] = useState<any>([])
  const [deptList, setDeptList] = useState([])

  useEffect(() => {
    form.resetFields()
    if (!isEmpty(record)) {
      form?.setFieldsValue(record)
    } else {
      form?.setFieldsValue({orgId: user.companyId})
      changeOrgId(user.companyId)
    }
  }, [record]);

  const getOptions = async () => {
    const seatConfigList = await getSeatIpConfigList();
    const orgList = await httpOrgList();
    const brandEnum = await getEnums('crm-tools', 'com.shj.saas.crm.tools.enums.seat.BrandEnum');
    return Promise.resolve({ seatConfigList, orgList, brandEnum })
  }

  const changeOrgId = (v) => {
    setDeptList([])
    form.setFieldsValue({deptId: null, userId: null, workName: '' });
    if (v) {
      departmentSearchList({companyId: v}).then(res => {
        setDeptList(isArray(res) ? res : [] )
      })
    }
  }

  const changeDeptId = (v) => {
    setSeatPerson([])
    form.setFieldsValue({userId: null, workName: ''})
    getSeatWorkerList({ userIdentityFilter: {orgId: v} }).then(res =>{
      setSeatPerson(res)
    })
  }

  const onSubmit = () => {
    form.validateFields().then(values => {
      setBtnLoading(true)
      const apiUrl = values.id ? seatAccountUpdate : seatAccountSave
      apiUrl(values).then((res: any) => {
        message.success(res.message || '操作成功');
        setOpen?.(false)
        onReload?.()
      }).finally(() => {
        setBtnLoading(false)
      })
    })
  }

  return (
    <SDrawer
      title={(record.id ? '编辑' : '新增')}
      open={open}
      onClose={() => setOpen?.(false)}
      onCancel={() => setOpen?.(false)}
      loading={btnLoading}
      onSubmit={onSubmit}
      destroyOnClose
      width={600}
    >
      <SGeneralForms
        formConfig={{form}}
        httpOptionsFn={getOptions}
        layout={'vertical'}
        configs={[
          {
            formItem: [
              { label: 'id', name: 'id', hidden: true },
              {
                label: '外呼服务商',
                name: 'brand',
                required: true,
                rules: [{ required: true, message: '外呼服务商不能为空' }],
                spanCol: 24,
                type: 'select',
                data: 'brandEnum',
                comConfig: {
                  fieldNames: { label: 'description', value: 'key' },
                }
              },
              {
                label: '智通是否回拨',
                name: 'zitongCallReturn',
                isDeps: true,
                deps: (form) => form.getFieldValue('brand') === 'ZITONG',
                required: true,
                rules: [{ required: true, message: '智通是否回拨不能为空' }],
                spanCol: 24,
                type: 'select',
                comConfig: {
                  options: [
                    { label: '是', value: true },
                    { label: '否', value: false },
                  ],
                },
              },
              {
                label: '通道名称',
                name: 'configId',
                required: true,
                rules: [{ required: true, message: '通道名称不能为空' }],
                isDeps: true,
                deps: (form) => form.getFieldValue('brand') === 'HELI',
                spanCol: 24,
                type: 'select',
                data: 'seatConfigList',
                comConfig: { fieldNames: { label: 'name', value: 'id' } }
              },
              {
                label: '分公司',
                name: 'orgId',
                required: true,
                rules: [{ required: true, message: '分公司不能为空' }],
                spanCol: 24,
                type: 'select',
                data: 'orgList',
                comConfig: {
                  fieldNames: { label: 'name', value: 'id' },
                  onChange: changeOrgId,
                }
              },
              {
                label: '部门',
                name: 'deptId',
                required: true,
                rules: [{ required: true, message: '部门不能为空' }],
                spanCol: 24,
                type: 'select',
                comConfig: {
                  options: deptList,
                  fieldNames: { label: 'departmentName', value: 'id' },
                  onChange: changeDeptId,
                  notFoundContent: '请选择分公司或无数据',
                }
              },
              {
                label: '员工姓名',
                name: 'userId',
                required: true,
                rules: [{ required: true, message: '员工姓名不能为空' }],
                spanCol: 24,
                type: 'select',
                comConfig: {
                  options: seatPerson,
                  notFoundContent: '请选择部门或无数据',
                  fieldNames: { label: 'userName', value: 'ucenterUserId' },
                  onChange: (v, op) => {
                    form?.setFieldValue('workerName', op?.userName);
                  }
                }
              },
              {
                name: 'workerName',
                hidden: true,
              },
              {
                label: '员工登录ID',
                name: 'userId',
                required: true,
                placeholder: '自动根据员工姓名带出',
                rules: [{ required: true, message: '员工登录ID不能为空' }],
                spanCol: 24,
                comConfig: {
                  disabled: true,
                }
              },
              {
                label: '登录账号',
                name: 'loginAccount',
                required: true,
                rules: [{ required: true, message: '登录账号不能为空' }],
                isDeps: true,
                deps: (form) => form.getFieldValue('brand') === 'HELI',
                spanCol: 24,
                comConfig: {
                  showCount: true, maxLength: 50,
                  onChange: (e) => form?.setFieldValue('loginAccount', trimStr(e.target.value))
                }
              },
              {
                label: '第三方呼叫账号',
                name: 'thirdAccount',
                required: true,
                rules: [{ required: true, message: '第三方呼叫账号不能为空' }],
                isDeps: true,
                deps: (form) => form.getFieldValue('brand') === 'HELI',
                spanCol: 24,
                comConfig: {
                  showCount: true, maxLength: 50,
                  onChange: (e) => form?.setFieldValue('thirdAccount', trimStr(e.target.value))
                }
              },
              {
                label: '第三方呼叫账号密码',
                name: 'password',
                required: true,
                rules: [{ required: true, message: '第三方呼叫账号密码不能为空' }],
                isDeps: true,
                deps: (form) => form.getFieldValue('brand') === 'HELI',
                spanCol: 24,
                comConfig: {
                  showCount: true, maxLength: 50,
                  onChange: (e) => form?.setFieldValue('password', trimStr(e.target.value))
                }
              },
            ],
          },
        ]}
      />
    </SDrawer>
  )
}